import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Button } from '@/components/Button';
import { Select } from '@/components/Select';
import { listPushTestApps, sendTestPush, type PushApp, type PushTestChannel } from '@/api/push';

const inputClass =
  'block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm placeholder-content-subtle ' +
  'focus:outline-none focus:ring-primary focus:border-primary sm:text-sm';

export function PushTest() {
  const { data, isLoading } = useQuery({ queryKey: ['push-test-apps'], queryFn: listPushTestApps });
  const apps: PushApp[] = data?.apps ?? [];

  const [appId, setAppId] = useState('');
  const [target, setTarget] = useState('');
  const [title, setTitle] = useState('Test push');
  const [body, setBody] = useState('It works ✓');
  const [channel, setChannel] = useState<PushTestChannel>('auto');
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState('');

  const selected = apps.find((a) => a.id === appId) ?? null;

  const sendMut = useMutation({
    mutationFn: () =>
      sendTestPush(appId, { target: target.trim(), title: title.trim(), body: body.trim(), channel }),
    onSuccess: (data) => setResult(JSON.stringify(data, null, 2)),
    onError: (err: Error) => setError(err.message),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResult(null);
    if (!appId) { setError('Select an app'); return; }
    if (!target.trim()) { setError('FCM token or device ID is required'); return; }
    sendMut.mutate();
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-semibold text-content">Push Test</h1>
        <p className="text-sm text-content-subtle mt-1">
          Send a single push to one device — paste an FCM registration token or a device ID.
          Test sends are not recorded in push analytics.
        </p>
      </div>

      <form onSubmit={submit} className="max-w-xl space-y-4 bg-surface border border-border rounded-lg p-5">
        {error && (
          <div className="p-3 bg-danger-soft border border-danger/30 rounded-md text-sm text-danger">{error}</div>
        )}

        <div>
          <label className="block text-sm font-medium text-content-muted mb-1">App</label>
          <Select
            value={appId}
            onChange={setAppId}
            placeholder={isLoading ? 'Loading…' : '— select an app —'}
            options={apps.map((a) => ({ value: a.id, label: `${a.name} (${a.bundle_id})` }))}
            searchable
          />
          {selected && !selected.push_firebase_account_id && (
            <p className="mt-1 text-xs text-danger">
              This app has no Firebase account configured — sending will fail.
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-content-muted mb-1">FCM token or device ID</label>
          <input
            type="text"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder="FCM registration token or device UUID"
            className={`${inputClass} font-mono`}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-content-muted mb-1">Title</label>
          <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} className={inputClass} />
        </div>

        <div>
          <label className="block text-sm font-medium text-content-muted mb-1">Body</label>
          <input type="text" value={body} onChange={(e) => setBody(e.target.value)} className={inputClass} />
        </div>

        <div>
          <label className="block text-sm font-medium text-content-muted mb-1">Channel</label>
          <Select
            value={channel}
            onChange={(v) => setChannel(v as PushTestChannel)}
            options={[
              { value: 'auto', label: 'Auto (from device record)' },
              { value: 'native', label: 'Native (Android/iOS SDK)' },
              { value: 'web', label: 'Web push (browser / PWA)' },
            ]}
          />
          <p className="mt-1 text-xs text-content-subtle">
            A pasted token we have no device record for defaults to native. Pick "Web push" when testing a
            browser or PWA token — sent as native, it will not render.
          </p>
        </div>

        <div className="flex justify-end">
          <Button type="submit" loading={sendMut.isPending}>Send push</Button>
        </div>

        {result && (
          <div>
            <p className="text-xs font-medium text-content-muted mb-1">Result:</p>
            <pre className="bg-surface-muted border border-border rounded-md p-3 text-xs overflow-x-auto whitespace-pre-wrap break-all">
              {result}
            </pre>
          </div>
        )}
      </form>
    </div>
  );
}
